import {
  BAG_PRESETS,
  DEFAULT_BAG_PRESET,
  VOWELS,
} from '../config/gameConfig';

const CLASSIC_BAG_DISTRIBUTION = {
  A: 9, B: 2, C: 2, D: 4, E: 12, F: 2, G: 3, H: 2,
  I: 9, J: 1, K: 1, L: 4, M: 2, N: 6, O: 8, P: 2,
  Q: 1, R: 6, S: 4, T: 6, U: 4, V: 2, W: 2, X: 1,
  Y: 2, Z: 1,
};

const CLASSIC_BAG_SIZE = Object.values(CLASSIC_BAG_DISTRIBUTION).reduce((sum, n) => sum + n, 0);

const randomIndex = (length) => Math.floor(Math.random() * length);
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const TEST_BAG_TILES = [
  {
    id: 'bag-Q',
    letter: 'Q',
    modifiers: { score: 0, multiplier: 2, extras: [] },
  },
  {
    id: 'bag-U',
    letter: 'U',
    modifiers: { score: 0, multiplier: 2, extras: [] },
  },
  {
    id: 'bag-I',
    letter: 'I',
    modifiers: { score: 0, multiplier: 2, extras: [] },
  },
  {
    id: 'bag-Z',
    letter: 'Z',
    modifiers: { score: 0, multiplier: 2, extras: [] },
  },
];

const cloneTile = (tile) => ({
  ...tile,
  modifiers: tile?.modifiers && typeof tile.modifiers === 'object' && !Array.isArray(tile.modifiers)
    ? { ...tile.modifiers, extras: Array.isArray(tile.modifiers.extras) ? [...tile.modifiers.extras] : [] }
    : { score: 0, multiplier: 1, extras: [] },
});

const shuffle = (items) => {
  const next = [...items];

  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1);
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }

  return next;
};

const createClassicBag = () => {
  const tiles = [];
  Object.entries(CLASSIC_BAG_DISTRIBUTION).forEach(([letter, count]) => {
    for (let i = 1; i <= count; i += 1) {
      tiles.push({
        id: `bag-classic-${letter}-${i}`,
        letter,
        modifiers: { score: 0, multiplier: 1, extras: [] },
      });
    }
  });
  return shuffle(tiles);
};

const createRandomBag = ({ size = CLASSIC_BAG_SIZE } = {}) => {
  const normalizedSize = Math.max(1, Number(size) || CLASSIC_BAG_SIZE);
  const minVowels = Math.round(normalizedSize * 0.42);
  const normalizedMinVowels = Math.min(normalizedSize, minVowels);

  const vowelLetters = Array.from({ length: normalizedMinVowels }).map(
    () => VOWELS[randomIndex(VOWELS.length)]
  );
  const remainingLetters = Array.from({ length: normalizedSize - normalizedMinVowels }).map(
    () => ALPHABET[randomIndex(ALPHABET.length)]
  );

  return shuffle([...vowelLetters, ...remainingLetters]).map((letter, index) => ({
    id: `bag-random-${letter}-${index + 1}`,
    letter,
    modifiers: { score: 0, multiplier: 1, extras: [] },
  }));
};

const createTestBag = () => TEST_BAG_TILES.map(cloneTile);

export const createInitialBag = (presetId = DEFAULT_BAG_PRESET) => {
  switch (String(presetId || '').toLowerCase()) {
    case BAG_PRESETS.RANDOM.id:
      return createRandomBag().map(cloneTile);
    case BAG_PRESETS.TEST.id:
      return createTestBag();
    case BAG_PRESETS.CLASSIC.id:
    default:
      return createClassicBag().map(cloneTile);
  }
};

export const drawTiles = (bagTiles, count) => {
  const bag = bagTiles.map(cloneTile);
  const drawn = [];

  while (drawn.length < count && bag.length) {
    const index = randomIndex(bag.length);
    drawn.push(bag[index]);
    bag.splice(index, 1);
  }

  return { bag, drawn };
};

export const returnTilesToBag = (bagTiles, tiles) => [...bagTiles, ...tiles].map(cloneTile);

export const createTilesFromLegacyLetters = (letters, idFactory) =>
  letters.map((letter) => ({
    id: idFactory(),
    letter,
    modifiers: { score: 0, multiplier: 1, extras: [] },
  }));